import { styled } from '@mui/material/styles';
import { useState, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ListItemButton as MuiListItemButton,
  Typography as MuiTypography,
  ListItemIcon as MuiListItemIcon,
  SvgIcon as MuiSvgIcon,
  ListItemText,
  Collapse,
  List,
  Stack,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import { WorkspaceSubMenuItem as WorkspaceSubMenuItemCopy } from './WorkspaceSubMenuItem';

const SvgIcon = styled(MuiSvgIcon, {
  shouldForwardProp: (prop) => prop !== 'wide' && prop !== 'selected',
})(({ wide, selected }) => ({
  color: selected ? '#2b63a4' : '#5f6368',
  width: '0.8em',
  height: '0.8em',
  ...(wide && {
    paddingLeft: '4px',
  }),
}));

const ListItemIcon = styled(MuiListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'wide' && prop !== 'selected',
})(({ wide, selected }) => ({
  color: selected ? '#2b63a4' : '#5f6368',
  '& svg': {
    width: '0.835em',
    height: '0.835em',
  },
  ...(wide && {
    paddingLeft: '4px',
  }),
}));

const ListItemButton = styled(MuiListItemButton, {
  shouldForwardProp: (prop) => prop !== 'wide',
})(({ wide }) => ({
  borderRadius: '6px',
  marginTop: '6px',
  minWidth: 'fit-content',
  '&.Mui-selected': {
    backgroundColor: '#a7c4e5',
    fontSize: '18px',
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: '#a7c4e5',
    },
    '.MuiTypography-root': {
      color: '#2b63a4',
    },
  },
  '&:hover': {
    backgroundColor: '#a7c4e5',
    fontSize: '18px',
    borderRadius: '6px',
    '.MuiTypography-root': {
      color: '#2b63a4',
    },
    '.MuiSvgIcon-root': {
      color: '#2b63a4',
    },
  },
  padding: '8px 10px',
  ...(wide && {
    padding: '8px 6px',
  }),
}));

const Typography = styled(MuiTypography, {
  shouldForwardProp: (prop) => prop !== 'wide' && prop !== 'selected',
})(({ wide, selected }) => ({
  fontSize: '14px',
  fontWeight: '400',
  ...(!wide && {
    paddingLeft: '9px',
    display: 'none',
  }),
  ...(selected && {
    color: '#2b63a4',
  }),
}));

export const WorkspaceSubMenuItem = ({ item, wide, sx }) => {
  const location = useLocation();
  const isSelected = useCallback(
    (item) => {
      const pathnames = location.pathname.split('/').filter((x) => x);
      const selected = pathnames.includes(item.id);
      return selected;
    },
    [location]
  );
  const selected = useMemo(() => {
    return isSelected(item);
  }, [isSelected, item]);

  const [open, setOpen] = useState(false);

  const handleOpenOnClick = () => {
    setOpen(!open);
  };

  return (
    <Stack sx={{ width: '100%' }}>
      <ListItemButton
        component={Link}
        to={`workspace/${item.id}`}
        sx={sx}
        selected={selected}
        onDoubleClick={handleOpenOnClick}
      >
        {open ? (
          <SvgIcon
            wide={wide}
            selected={selected}
            onClick={handleOpenOnClick}
          >
            <ArrowDropDownIcon />
          </SvgIcon>
        ) : (
          <SvgIcon
            wide={wide}
            selected={selected}
            onClick={handleOpenOnClick}
          >
            <ArrowRightIcon />
          </SvgIcon>
        )}
        <ListItemIcon
          wide={wide}
          selected={selected}
        >
          <FolderOpenOutlinedIcon />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant="subtitle2"
              wide={wide}
              selected={selected}
              // noWrap={true}
            >
              {item.name}
            </Typography>
          }
        />
      </ListItemButton>
      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >
          {item.children.map((item) => (
            <WorkspaceSubMenuItemCopy
              item={item}
              key={item.id}
              wide={wide}
              sx={{ pl: item.padding, py: 0, minHeight: 32 }}
            />
          ))}
        </List>
      </Collapse>
    </Stack>
  );
};
